// ── "The system is being updated" (Bugify deploy-now, owner 2026-11-03) ───────────────────────────────────────────
// "注意部署时，增加等待进度条，确保客户看起来不是系统死机，用户知道当前正在部署。" Mounted once in App, outside every
// gate, so the staff pages, the phone apps, the sign-in page and the clients' portal all carry it. What it draws comes
// from services/apiHealth.ts:
//   • a small pill at the top while the server says a deploy is on, in words that match the moment (utils/deployStatus.ts
//     noticeLine): "save your work, it restarts shortly" while it builds; "restarting to finish an update, back in a few
//     seconds" during the restart; "back — finishing the update" once the API answers again — unless this tab's card
//     has just said "Back online" for that restart, when it says nothing more. Its "Elapsed" counts the deploy, or the
//     restart alone during the restart (noticeSince). It blocks nothing outside itself and can be closed;
//   • a card over the whole page while THIS installation's API does not answer during a deploy — a moving bar, the time
//     it has taken, and "it reconnects by itself". One failed /api/health probe brings it up while the file says the
//     server is restarting (live check 2026-11-04: it waited for a second one, which had already succeeded, and never
//     showed); two while it says "deploying" or a deploy was seen lately (a network blip during minutes of building must
//     not block the page). It never reloads the page on its own and never clears what was typed: the page underneath is
//     only made unreachable (inert, and keys stopped) until the API is back, then the card says "Back online" for a
//     moment (up at least 2.5 s in all, never a flash) and goes — "the update has finished" once the file is idle and
//     the outage was the restart's, "you can carry on" while deploy.sh is still checking. After three minutes it offers
//     Try now / Reload / Continue anyway, so nobody is ever stuck behind it.
//     An outage no deploy explains (a lost network) never blocks: it is the "Reconnecting…" banner (apiHealth isOverlay);
//   • "A new version is ready — Update" once the API is back on a newer build (or a piece of the old one has gone).
// A portal pinned to ANOTHER installation (a client of that one, signed in through this installation's portal) is never
// blocked by this installation's restart: every request of theirs goes to their own installation. They get the pill,
// and the "Reconnecting…" banner if their own installation stops answering (portalPinnedElsewhere below, review round 2).
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useStore } from '../store';
import { loadedBuild } from '../utils/appUpdate';
import { portalApiBase } from '../utils/api';
import { estimatedPct, fmtElapsed, noticeLine, noticeSince, DEPLOY_STATUS_MAX_AGE_MS } from '../utils/deployStatus';
import { ApiHealth, getApiHealth, subscribeApiHealth, startApiHealth, probeNow, dismissOverlay, deployExplains, statusNow, ownApiBase } from '../services/apiHealth';

const SLOW_MS = 3 * 60_000;           // after this the card offers its way out
const srOnly: React.CSSProperties = { position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0 };
const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Is this a portal page whose client is pinned to ANOTHER installation (utils/api.ts PORTAL_SYSTEMS / baseFor)? Then
 *  this installation's restart is not theirs to wait for: services/apiHealth.ts arms no restart watch and raises no
 *  card for it (review round 2: Samui's portal blocked its JT and Daybook clients for every Samui restart). */
const portalPinnedElsewhere = (): boolean => {
  try { return window.location.pathname.indexOf('/portal') === 0 && portalApiBase() !== ownApiBase(); } catch { return false; }
};

function useApiHealth(): ApiHealth {
  const [s, setS] = useState<ApiHealth>(getApiHealth);
  useEffect(() => subscribeApiHealth(setS), []);
  return s;
}

/** Redraws once a second while `on` — the elapsed counters are the screen's proof of life. */
function useTick(on: boolean): void {
  const [, set] = useState(0);
  useEffect(() => {
    if (!on) return;
    const id = window.setInterval(() => set(n => n + 1), 1000);
    return () => window.clearInterval(id);
  }, [on]);
}

export default function DeployNotice({ isPhoneApp }: { isPhoneApp: (pathname: string) => boolean }) {
  const { t } = useTranslation();
  const loc = useLocation();
  const h = useApiHealth();
  // An email waiting out its Undo send lives in this page; a reload would lose it (as utils/appUpdate.tsx).
  const sending = useStore(s => s.sendQueue.length > 0);
  const [closed, setClosed] = useState('');        // the pill the person closed: 'd:<startedAt>' or 't:<since>'
  const [pillClosed, setPillClosed] = useState(''); // the build whose reload offer was closed

  useEffect(() => {
    // React is up, so the boot script's "reloading by itself" count (public/index.html) starts again from nothing.
    try { sessionStorage.removeItem('slf_boot_retry'); } catch { /* storage off */ }
    startApiHealth(loadedBuild, portalPinnedElsewhere);
  }, []);

  // What the file says now, as a RECENT read shows it (apiHealth statusNow): a "deploying" remembered from before the
  // laptop slept is no reason for a pill.
  const live = statusNow(h);
  const deploying = live !== 'idle';
  const trouble = h.probe.down && !h.overlay;   // an outage no deploy explains, another installation's API, or "Continue anyway"
  // The deploy's FIRST startedAt (apiHealth busySince): its "restarting" write must neither reset the counter nor bring
  // back a pill the person closed.
  const since = h.busySince || h.status.startedAt;
  const key = trouble ? `t:${h.probe.since}` : deploying ? `d:${since}` : '';
  // The sentence follows the file (live check 2026-11-04: "unavailable shortly" was shown after the restart had happened):
  // deploying → save your work; restarting → back in a few seconds; restarting with the API answering again (deploy.sh
  // writes idle only after its own health check) → finishing, or nothing once the card has said "Back online".
  const line = noticeLine(live, h.restartBack, h.backSaid);
  const banner: '' | 'trouble' | 'back' | 'deploying' = h.overlay ? ''
    : trouble ? (closed === key ? '' : 'trouble')
    : h.back === 'banner' ? 'back'
    : line && closed !== key ? 'deploying' : '';
  const buildKey = h.status.build || 'chunk';
  const pill = h.newBuild && !h.overlay && !h.probe.down && !deploying && !sending && !isPhoneApp(loc.pathname) && pillClosed !== buildKey;
  useTick(!!banner);

  const serverNow = Date.now() + h.serverOffsetMs;
  const counterFrom = banner === 'deploying' ? noticeSince(line, h.busySince, h.status.startedAt) : 0;
  const elapsed = banner === 'trouble' ? Date.now() - h.probe.since
    : counterFrom ? serverNow - counterFrom : -1;
  const sentence = banner === 'trouble' ? t('sysup_reconnecting') : banner === 'back' ? t('sysup_back')
    : banner === 'deploying' ? (line === 'finishing' ? t('sysup_finishing') : line === 'restarting' ? t('sysup_restarting') : t('sysup_banner')) : '';

  const reload = () => {
    try { if (h.status.build) sessionStorage.setItem('slf_reloaded_for', h.status.build); } catch { /* storage off: the offer may come back once */ }
    window.location.reload();
  };

  return (
    <>
      {/* The sentence alone, for a screen reader: the counter beside it would be read out every second. */}
      <div aria-live="polite" style={srOnly}>{sentence}</div>
      {(banner || pill) && (
        <div style={{ position: 'fixed', top: 'calc(env(safe-area-inset-top) + 8px)', left: '50%', transform: 'translateX(-50%)', width: 'min(560px, calc(100vw - 32px))', zIndex: 29990, display: 'flex', flexDirection: 'column', gap: 8, pointerEvents: 'none' }}>
          {banner && (
            <div style={{ pointerEvents: 'auto', display: 'flex', alignItems: 'center', gap: 10, padding: '9px 10px 9px 14px', borderRadius: 12, boxShadow: '0 8px 24px rgba(15,23,42,0.25)', fontSize: 13, lineHeight: 1.45,
              background: banner === 'back' ? '#15803D' : '#1E293B', color: '#fff' }}>
              <i className={`ti ti-${banner === 'back' ? 'circle-check' : 'loader-2 mf-spin'}`} style={{ fontSize: 17, flexShrink: 0 }} aria-hidden />
              <span style={{ flex: 1, minWidth: 0 }}>{sentence}</span>
              {elapsed >= 0 && <span aria-hidden style={{ flexShrink: 0, fontVariantNumeric: 'tabular-nums', opacity: 0.75, fontSize: 12 }}>{t('sysup_elapsed', { time: fmtElapsed(elapsed) })}</span>}
              {banner !== 'back' && (
                <button onClick={() => setClosed(key)} aria-label={t('sysup_dismiss')} title={t('sysup_dismiss')}
                  style={{ flexShrink: 0, width: 26, height: 26, border: 'none', borderRadius: 6, background: 'transparent', color: '#fff', opacity: 0.75, cursor: 'pointer' }}>
                  <i className="ti ti-x" aria-hidden />
                </button>
              )}
            </div>
          )}
          {pill && (
            <div role="status" style={{ pointerEvents: 'auto', display: 'flex', alignItems: 'center', gap: 10, padding: '7px 8px 7px 14px', borderRadius: 12, border: '1px solid var(--accent)', background: 'var(--accent-bg)', color: 'var(--accent-text)', boxShadow: '0 8px 24px rgba(15,23,42,0.18)' }}>
              <i className="ti ti-refresh" style={{ fontSize: 17, flexShrink: 0 }} aria-hidden />
              <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: 600 }}>{t('app_update_ready')}</span>
              <button onClick={reload}
                style={{ flexShrink: 0, padding: '6px 14px', border: 'none', borderRadius: 'var(--radius-md)', background: 'var(--accent)', color: '#fff', fontSize: 13.5, fontWeight: 600, cursor: 'pointer' }}>
                {t('app_update_now')}
              </button>
              <button onClick={() => setPillClosed(buildKey)} aria-label={t('sysup_dismiss')} title={t('sysup_dismiss')}
                style={{ flexShrink: 0, width: 26, height: 26, border: 'none', borderRadius: 6, background: 'transparent', color: 'inherit', cursor: 'pointer' }}>
                <i className="ti ti-x" aria-hidden />
              </button>
            </div>
          )}
        </div>
      )}
      {h.overlay && <Overlay h={h} />}
    </>
  );
}

/** The blocking card: only while this installation's own API does not answer. */
function Overlay({ h }: { h: ApiHealth }) {
  const { t } = useTranslation();
  const card = useRef<HTMLDivElement | null>(null);
  // The outage's start, kept once the probe has reset — so "Back online" does not show the counter jumping to 0:00. During
  // a restart it is the restart's own start (the file's "restarting" write, server clock) when that is earlier: a tab
  // that only noticed 10 s in shows 0:10, and its bar is as far along as the restart is, not back at the beginning.
  const live = statusNow(h);
  const restartAt = live === 'restarting' && h.status.startedAt ? h.status.startedAt - h.serverOffsetMs : 0;
  const from = Math.min(h.probe.since || Infinity, restartAt > 0 && Date.now() - restartAt < DEPLOY_STATUS_MAX_AGE_MS ? restartAt : Infinity);
  const since = useRef(isFinite(from) ? from : Date.now());
  if (isFinite(from) && from < since.current) since.current = from;
  useTick(true);

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const root = document.getElementById('root');
    const wasInert = !!root && root.hasAttribute('inert');
    if (root && !wasInert) root.setAttribute('inert', '');
    card.current?.focus();
    // `inert` keeps clicks and focus out of the page, but not the listeners the Email, Portal and Calendar pages keep on
    // `window` for their shortcuts: every key stops here, and a key aimed outside the card does nothing at all.
    const onKey = (e: KeyboardEvent) => {
      const box = card.current;
      const inside = !!box && box.contains(e.target as Node);
      e.stopPropagation();
      if (e.key === 'Tab' && box) {
        const list = Array.from(box.querySelectorAll<HTMLElement>(FOCUSABLE));
        const active = document.activeElement;
        if (!list.length) { e.preventDefault(); box.focus(); return; }
        const first = list[0], last = list[list.length - 1];
        if (e.shiftKey ? (!inside || active === first || active === box) : (!inside || active === last)) {
          e.preventDefault();
          (e.shiftKey ? last : first).focus();
        }
        return;
      }
      if (!inside) e.preventDefault();
    };
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      if (root && !wasInert) root.removeAttribute('inert');
      try { opener?.focus?.(); } catch { /* the opener is gone */ }
    };
  }, []);

  const back = h.back === 'overlay';
  const elapsed = Math.max(0, Date.now() - since.current);
  const slow = !back && elapsed >= SLOW_MS;
  // "Updating" when a deploy was actually seen lately — the only time the card comes up at all now; the plain-outage
  // words stay for the moment a deploy's freshness runs out while the card is still up.
  const updating = deployExplains(h);
  // What "Back online" adds (review round 1: it said "the update has finished" while the file still said restarting, and
  // after a plain crash that only the 15-min "deploy seen lately" rule called a deploy's):
  //   • the outage was the restart's and the file is idle again → "the update has finished — you can carry on";
  //   • the outage was the restart's, the file still says restarting (deploy.sh's own last check) → "you can carry on";
  //   • anything else (an outage while it was still building, a crash after it) → "Back online" alone.
  const backBody = !back || !updating || !h.restartOutage ? '' : live === 'idle' ? t('sysup_back_body') : t('sysup_back_carry_on');
  const pct = back ? 100 : estimatedPct(elapsed);
  const btn: React.CSSProperties = { padding: '8px 14px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', color: 'var(--text-primary)', fontSize: 13, fontWeight: 600, cursor: 'pointer' };

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 30000, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div ref={card} tabIndex={-1} role="alertdialog" aria-modal="true" aria-labelledby="sysup-title" aria-describedby="sysup-body"
        style={{ width: 'min(420px, calc(100vw - 32px))', background: 'var(--bg-app)', color: 'var(--text-primary)', borderRadius: 16, boxShadow: '0 18px 48px rgba(15,23,42,0.35)', padding: '22px 22px 18px', outline: 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <i className={`ti ti-${back ? 'circle-check' : 'loader-2 mf-spin'}`} style={{ fontSize: 22, color: back ? '#15803D' : 'var(--accent)' }} aria-hidden />
          <div id="sysup-title" style={{ fontSize: 16, fontWeight: 800 }}>{back ? t('sysup_back') : updating ? t('sysup_title') : t('sysup_lost_title')}</div>
        </div>
        <div id="sysup-body" style={{ fontSize: 13.5, lineHeight: 1.55, color: 'var(--text-secondary)', marginBottom: 14 }}>
          {back ? backBody : updating ? t('sysup_body') : t('sysup_lost_body')}
        </div>
        <div role="progressbar" aria-label={t('sysup_progress_label')} aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct}
          style={{ height: 8, borderRadius: 999, background: 'var(--bg-active)', overflow: 'hidden' }}>
          <div className="mf-stripe" style={{ width: `${pct}%`, height: '100%', borderRadius: 999, background: back ? '#15803D' : 'var(--accent)', transition: 'width 0.9s linear' }} />
        </div>
        <div aria-hidden style={{ marginTop: 7, fontSize: 12, color: 'var(--text-tertiary)', fontVariantNumeric: 'tabular-nums' }}>
          {t('sysup_elapsed', { time: fmtElapsed(elapsed) })}
        </div>
        {slow && (
          <>
            <div style={{ marginTop: 12, fontSize: 13, color: '#B45309' }}>{t('sysup_slow')}</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
              <button style={{ ...btn, background: 'var(--accent)', color: '#fff', border: 'none' }} onClick={probeNow}>{t('sysup_retry')}</button>
              <button style={btn} onClick={() => window.location.reload()}>{t('sysup_reload')}</button>
              <button style={btn} onClick={dismissOverlay}>{t('sysup_continue')}</button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
